import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import moment from "moment";
import { useLazyQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button, FormGroup, Select, showSnackbar, EncodedPolyLine,  MarkerProps } from "@cofano/cck";
import { Box, CircularProgress, Drawer, FormControlLabel, Radio, RadioGroup } from "@mui/material";

import Language from "components/utils/Language";
import { FormErrorMessage } from "components/form/FormErrorMessage";
import DatePicker from "components/cck/DatePicker";
import { request } from "components/utils/Request2";
import { showFile } from "components/filepreview/FilePreview";

import { ShipSelectOption } from "view/ships/components/selects/ShipSelect";

import { localeAsLanguageKey, locales } from "utils/locales";

import { HistoryItem, HistoryNodeItem, HourWindows, getHistoryNodes } from "../../utils/MapUtils";
import { mapShipHistorySchema, MapShipHistorySchemaInput, MapShipHistorySchemaOutput } from "../../utils/map.schema";
import MapShipHistoryTimeLine from "./MapShipHistoryTimeLine";
import SimpleLocationMarker from "../markers/SimpleLocationMarker";

import { GetHistoryByIdDocument } from "__generated__/view/maps/graphql/map.queries.generated";

const MapShipHistory = ({
	ship,
	onChange,
}: {
	ship?: ShipSelectOption;
	onChange: (markers: MarkerProps[], encodedPolylines: EncodedPolyLine[]) => void,
}) => {

	const [ history, setHistory ] = useState<HistoryItem | undefined>(undefined);
	const [ selectedNode, setSelectedNode ] = useState<HistoryNodeItem | undefined>(undefined);
	const [ geofences, setGeofences ] =  useState<string[]>([]);
	const [ isTimelineAvailable, setIsTimelineAvailable ] = useState<boolean>(false);
	const [ selectedHistoryTimeLineNode, setSelectedHistoryTimeLineNode ] =  useState<HistoryNodeItem | undefined>(undefined);

	const { handleSubmit, control, setValue, watch } = useForm<MapShipHistorySchemaInput, unknown, MapShipHistorySchemaOutput>({
		resolver: zodResolver(mapShipHistorySchema),
		defaultValues: {
			hourWindow: HourWindows.TWELVE,
			from: moment().add(-12, "hours").unix() * 1000,
			to: moment().unix() * 1000,
		},
	});

	const values = watch();

	const [ getHistoryById ] = useLazyQuery(GetHistoryByIdDocument);

	const search = (data: MapShipHistorySchemaOutput) => {
		setSelectedNode(undefined);
		setHistory(undefined);
		setSelectedHistoryTimeLineNode(undefined);

		if (!ship?.id) {
			showSnackbar(intl.formatMessage({ id: "SELECT_SHIP_FIRST" }), { variant: "warning" });
			return;
		}
		if (!data.from || !data.to) {
			showSnackbar(intl.formatMessage({ id: "REQUIRED_DATES" }), { variant: "warning" });
			return;
		}

		getHistoryById({
			variables: {
				shipId: ship.id,
				from: data.from,
				to: data.to,
			},
		}).then(({ data }) => {
			const voyages = data?.getHistoryById?.voyages ?? [];

			if (voyages.length === 0) {
				setHistory(undefined);
				showSnackbar(intl.formatMessage({ id: "BERTH_INFO_HISTORY_NOT_FOUND" }), { variant: "warning" });
				return;
			}

			if (data?.getHistoryById) {
				setHistory(data?.getHistoryById);
			}
		});
	};

	const handleTimelineUpdate = (
		selectedNode?: HistoryNodeItem,
		geofences?: string[],
		timelineAvailable?: boolean,
	) => {
		setSelectedNode(selectedNode);
		setGeofences(geofences || []);
		setIsTimelineAvailable(timelineAvailable || false);
	};

	const createMapItems = () => {

		const markers: MarkerProps[] = [];
		const encodedPolylines: EncodedPolyLine[] = [];
		const nodes = selectedNode ? [ selectedNode ] : history ?  getHistoryNodes(history) : [];

		if (nodes.length === 0) {
			onChange(markers, encodedPolylines);
			return;
		}

		nodes.forEach((node, index) => {
			if (node.type === "STOP" && node.location?.latitude && node.location?.longitude) {
				markers.push({
					lat: node.location?.latitude,
					lng: node.location?.longitude,
					component: () => <SimpleLocationMarker key={index} />,
				});

				if (node.location?.encodedPolyline && geofences.find(g => g === node.id)) {
					encodedPolylines.push({
						color: "red",
						line: node.location.encodedPolyline,
						fillColor: "red",
						fillOpacity: 0.6,
					});
				}
			} else if (node.polyline) {
				encodedPolylines.push({
					color: "#1763A6",
					line: node.polyline,
				});
			}

			onChange(markers, encodedPolylines);
		});
	};

	const handleOnSelect = (node: HistoryNodeItem | undefined) => {
		setSelectedHistoryTimeLineNode(node);
	};

	useEffect(() => {
		if (!selectedHistoryTimeLineNode || !ship?.id) {
			return;
		}

		if (selectedHistoryTimeLineNode.startTime == null || selectedHistoryTimeLineNode.endTime == null) {
			return;
		}

		getHistoryById({
			variables: {
				shipId: ship.id,
				from: selectedHistoryTimeLineNode.startTime,
				to: selectedHistoryTimeLineNode.endTime,
			},
		}).then(({ data }) => {
			const voyages = data?.getHistoryById?.voyages ?? [];

			if (voyages.length === 0) {
				showSnackbar(intl.formatMessage({ id: "BERTH_INFO_HISTORY_NOT_FOUND" }), { variant: "warning" });
				setHistory(undefined);
				return;
			}
		});
	}, [ selectedHistoryTimeLineNode, ship?.id ]);

	useEffect(() => {
		createMapItems();
	}, [ history, selectedNode, geofences, selectedHistoryTimeLineNode ]);

	useEffect(() => {
		setValue("from", moment().subtract(values.hourWindow, "hours").unix() * 1000);
		setValue("to", moment().unix() * 1000);
	}, [ values.hourWindow ]);

	return (
		<React.Fragment>
			<form onSubmit={handleSubmit(search)}>
				<FormGroup
					label={Language("TOTAL_HOURS_BACK")}
					labelPlacement="left-center"
					overflow="hidden">
					<Controller
						name="hourWindow"
						control={control}
						render={({ field }) => (
							<Select
								{...field}
								clearable={false}
								fetchOptions={Object.values(HourWindows).filter((value) => typeof value === "number") as number[]} />
						)} />
				</FormGroup>

				<FormGroup
					label={Language("FROM")}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name="from"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<DatePicker {...field} showTimeInput />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>

				<FormGroup
					label={Language("TO")}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name="to"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<DatePicker {...field} showTimeInput />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>

				<Box sx={{ display: "flex", justifyContent: "space-between" }}>
					<Button type="submit" size="small" variant="outlined">
						{Language("SEARCH")}
					</Button>

					{isTimelineAvailable && (
						<HistoryExportPreview shipId={ship?.id ?? null} from={values.from} to={values.to} />
					)}
				</Box>
			</form>

			<MapShipHistoryTimeLine history={history} onTimelineUpdate={handleTimelineUpdate} onSelect={handleOnSelect} />
		</React.Fragment>
	);
};

const HistoryExportPreview = ({
	shipId,
	from,
	to,
}: {
	shipId: number | null;
	from: number | null;
	to: number | null;
}) => {

	const [ previewOpen, setPreviewOpen ] = useState(false);
	const [ preview, setPreview ] = useState<{ base64: string, type: "pdf" } | null>(null);
	const [ language, setLanguage ] = useState("en");
	const [ loading, setLoading ] = useState(false);

	const loadPreview = (languageCode: string = language) => {

		setPreviewOpen(true);
		setPreview(null);
		setLoading(true);

		const urlSearchParams = new URLSearchParams();
		if (from) {
			urlSearchParams.append("from", from.toString());
		}
		if (to) {
			urlSearchParams.append("to", to.toString());
		}
		urlSearchParams.append("languageCode", languageCode);

		request<{ pdf: string }>({
			url: `history/${shipId}/export/preview?${urlSearchParams.toString()}`,
			method: "GET",
		}).then(response => {
			setPreview({ base64: response.pdf, type: "pdf" });
			setLoading(false);
		});
	};

	return shipId && (
		<>
			<Button
				onClick={() => loadPreview()}
				variant="outlined"
				size="small">
				{Language("MAPS_EXPORT_DATA")}
			</Button>

			<Drawer
				anchor="right"
				open={previewOpen}
				onClose={() => {
					setPreviewOpen(false);
					setPreview(null);
				}}>
				<Box p={2} sx={{ width: 700 }}>
					<RadioGroup
						value={language}
						onChange={event => {
							const languageCode = event.target.value;
							setLanguage(languageCode);
							loadPreview(languageCode);
						}}>
						{Object.values(locales).map((locale) => (
							<FormControlLabel
								key={locale}
								value={locale}
								control={<Radio />}
								label={localeAsLanguageKey(locale)}
								disabled={loading}
							/>
						))}
					</RadioGroup>
					{preview && !loading ? showFile(preview.base64, preview.type) : <CircularProgress size={24} />}
				</Box>
			</Drawer>
		</>
	);
};

export default MapShipHistory;
